import { UAParser } from 'ua-parser-js';

/**
 * BonsaitUtils - General utility functions
 * 
 * Provides helpers for:
 * - Browser/device detection
 * - Cookie management
 * - URL parameter parsing
 * - Number parsing
 */
export class BonsaitUtils {
    
    // ===== Browser & Device Detection =====
    
    /**
     * Get browser information
     * @returns {Object} Browser object with name, version, major
     */
    static getBrowser() {
        const { browser } = UAParser(window.navigator.userAgent);
        return browser;
    }

    /**
     * Check if device is mobile/touch-enabled
     * @returns {boolean} True if mobile device
     */
    static isMobile() {
        return (
            ('ontouchstart' in window) || 
            (navigator.maxTouchPoints > 0) || 
            (navigator.msMaxTouchPoints > 0)
        );
    }

    /**
     * Get device information
     * @returns {Object} Device object with vendor, model, type
     */
    static getDevice() {
        const { device } = UAParser(window.navigator.userAgent);
        return device;
    }

    // ===== Cookie Management =====

    /**
     * Set a cookie
     * @param {string} name - Cookie name
     * @param {string} value - Cookie value
     * @param {number} [days=365] - Number of days until expiration
     */
    static setCookie(name, value, days = 365) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "expires=" + date.toUTCString();
        document.cookie = `${name}=${value};${expires};path=/`;
    }

    /**
     * Get a cookie value
     * @param {string} name - Cookie name
     * @returns {string} Cookie value or empty string if not found
     */
    static getCookie(name) {
        const nameEQ = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookies = decodedCookie.split(';');
        
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length, cookie.length);
            }
        }
        return "";
    }

    /**
     * Delete a cookie
     * @param {string} name - Cookie name
     */
    static deleteCookie(name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    // ===== URL Parameters =====

    /**
     * Get URL parameter value by name
     * @param {string} name - Parameter name
     * @param {string} [url=window.location.href] - URL to parse (defaults to current page)
     * @returns {string|null} Parameter value or null if not found
     */
    static getParameterByName(name, url = window.location.href) {
        name = name.replace(/[\[\]]/g, "\\$&");
        const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)", "i");
        const results = regex.exec(url);
        
        if (!results) return null;
        if (!results[2]) return '';
        
        return decodeURIComponent(results[2].replace(/\+/g, " "));
    }

    /**
     * Get all URL parameters as an object
     * @param {string} [url=window.location.href] - URL to parse
     * @returns {Object} Object with parameter key-value pairs
     */
    static getAllParameters(url = window.location.href) {
        const params = {};
        const urlObj = new URL(url);
        
        for (const [key, value] of urlObj.searchParams) {
            params[key] = value;
        }
        
        return params;
    }

    // ===== Number Utilities =====

    /**
     * Parse a value to integer, removing non-numeric characters
     * @param {string|number} value - Value to parse
     * @returns {number} Parsed integer or 0 if invalid
     */
    static getNumberValue(value) {
        if (!value) return 0;
        
        try {
            // Remove all non-digit characters except minus and decimal point
            if (typeof value === 'string' || value instanceof String) {
                value = value.replace(/[^-\d\.]/g, '');
            }
            
            const parsed = parseFloat(value).toFixed(0);
            if (parsed) {
                return Number(parsed);
            }
        } catch (error) {
            console.warn('BonsaitUtils.getNumberValue: Failed to parse', value, error);
        }
        
        return 0;
    }

    /**
     * Format number with thousands separator
     * @param {number} num - Number to format
     * @param {string} [separator=','] - Thousands separator
     * @returns {string} Formatted number string
     */
    static formatNumber(num, separator = ',') {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    }

    // ===== Misc Utilities =====

    /**
     * Debounce a function
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    static debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Throttle a function
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in milliseconds
     * @returns {Function} Throttled function
     */
    static throttle(func, limit) {
        let inThrottle;
        return function(...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
}
